import React from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';

import Button from '@components/Atoms/Button/Button';
import { useFeatureFlagEnabled } from '@components/Atoms/FeatureFlagGuard/hooks';
import { requestDistrictReportCsv } from '@apis/districtReports';
import useExamReportCsvExport from '../../../hooks/useExamReportCsvExport';
import { EXAM_CSV_EXPORT_FEATURE } from '../ExportCsvButton';
import { ReportType } from './BaseDistrictReport';

interface DistrictExportCsvButtonProps {
  reportType: ReportType;
  districtId: number;
  examId?: number;
  subjectId?: string;
}

/**
 * Queues a CSV export of the district-wide report. Sits in the section header's
 * action slot, so the surrounding title already names the report type. Hidden
 * entirely unless the exam-results-csv-export flag is on for the district.
 */
const DistrictExportCsvButton: React.FC<DistrictExportCsvButtonProps> = ({
  reportType,
  districtId,
  examId,
  subjectId,
}) => {
  const isEnabled = useFeatureFlagEnabled({ featureName: EXAM_CSV_EXPORT_FEATURE });
  const { isExporting, startExport } = useExamReportCsvExport();

  if (!isEnabled) {
    return null;
  }

  return (
    <Button
      variant="outlined"
      size="small"
      disabled={isExporting}
      onClick={() =>
        startExport(() =>
          requestDistrictReportCsv({
            reportType,
            districtId,
            examId,
            curriculumsubjectIds: subjectId ? [subjectId] : [],
          }),
        )
      }
      startIcon={
        isExporting ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />
      }
    >
      {isExporting ? 'Preparing CSV...' : 'Export CSV'}
    </Button>
  );
};

export default DistrictExportCsvButton;
